import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import useScript from "../../../../hooks/useScript";
import { useLazyCreateOpaquePaymentQuery } from "../../../../store/services/PaymentService";
import { showToast } from "../../../../store/slices/toastSlice";
import { useTypedDispatch, useTypedSelector } from "../../../../store/store";
import TOAST from "../../../../utils/constants/toast";
import getMonthsList from "../../../../utils/helpers/getMonthsList";
import getYearsList from "../../../../utils/helpers/getYearsList";
import { prepareParams } from "../../../../utils/helpers/rooms";
import {
  validateCVV,
  validateCreditCard,
  validateDate,
} from "../../../../utils/validation/functions/payment";
import LoadingContainer from "../../../containers/LoadingContainer";
import Alert from "../../../shared/Alert";
import Button from "../../../shared/Button";
import CustomSelect from "../../../shared/CustomSelect";
import Input from "../../../shared/Input";
import { PaymentBillingDetails } from "../../index";

import LegalData from "./../../molecules/LegalData";
import PriceType from "./../../molecules/PriceType";
import styles from "./index.module.scss";

interface AcceptObject {
  dispatchData?: (secureData: any, responseHandler: any) => void;
}

export interface AuthorizeProviderProps {
  mode?: "page" | "modal";
  amount?: number;
  currency?: string;
  pnr?: string;
  source?: string;
  billingDetails?: PaymentBillingDetails;
  onSuccess?: () => void;
  onFail?: (message?: string) => void;
  hideTitleAndPriceType?: boolean;
}

function AuthorizePayment({
  mode = "page",
  amount,
  currency,
  pnr,
  source,
  billingDetails,
  onSuccess,
  onFail,
  hideTitleAndPriceType,
}: AuthorizeProviderProps) {
  const navigate = useNavigate();
  const dispatch = useTypedDispatch();
  const [createOpaqueData] = useLazyCreateOpaquePaymentQuery();

  const { cruiseId } = useParams();
  const [searchParams] = useSearchParams();

  const { isCruiseLoading } = useTypedSelector((state) => state.search);
  const { rooms } = useTypedSelector((state) => state.rooms);
  const { agency, agent } = useTypedSelector((state) => state.session);
  const { guests } = useTypedSelector((state) => state.guests);
  const { reservation } = useTypedSelector((state) => state.paymentModal);

  const {
    authorize_api_login_id,
    authorize_client_key,
    authorize_is_test,
    authorize_is_mine,
    payment_checkbox_1,
    payment_checkbox_2,
    payment_checkbox_3,
    payment_checkbox_4,
    payment_checkbox_5,
    payment_checkbox_6,
    bin_restriction,
    payment_option_text,
    new_booking_payment_info,
  } = useTypedSelector((state) => state.environment);

  const [priceType, setPriceType] = useState<"full" | "deposit" | string>(
    "deposit",
  );

  const [isLoading, setIsLoading] = useState(false);

  const [date, setDate] = useState({
    month: { value: "", errorMessage: "" },
    year: { value: "", errorMessage: "" },
  });

  const [card, setCard] = useState({
    cardNumber: { value: "", errorMessage: "" },
    expirationDate: {
      value: "",
      errorMessage: "",
    },
    cvv: { value: "", errorMessage: "" },
  });

  const [legalData, setLegalData] = useState({
    checkbox_1: !payment_checkbox_1,
    checkbox_2: !payment_checkbox_2,
    checkbox_3: !payment_checkbox_3,
    checkbox_4: !payment_checkbox_4,
    checkbox_5: !payment_checkbox_5,
    checkbox_6: !payment_checkbox_6,
  });

  const isUseAcceptCore = useMemo(
    () => [true, "true"].includes(authorize_is_mine),
    [authorize_is_mine],
  );

  const scriptUrl = useMemo(
    () =>
      [true, "true"].includes(authorize_is_test)
        ? "https://jstest.authorize.net/v1/Accept.js"
        : "https://js.authorize.net/v1/Accept.js",
    [authorize_is_test],
  );

  const [loading] = useScript(scriptUrl);

  const activeRestriction = useMemo(() => {
    const parsedRestrictions: Array<{
      number: string[];
      errorMessage: string;
    }> = JSON.parse(bin_restriction || "[]");

    const matchedRestriction = parsedRestrictions.find((bin) =>
      bin.number.some((number) => card.cardNumber.value.startsWith(number)),
    );

    return matchedRestriction;
  }, [card.cardNumber.value, bin_restriction]);

  const isFormValid = useMemo(() => {
    const isNumberValid =
      !!card.cardNumber.value && !card.cardNumber.errorMessage;

    const isExpDateValid =
      !!card.expirationDate.value && !card.expirationDate.errorMessage;

    const isCvvValid = !!card.cvv.value && !card.cvv.errorMessage;

    const isLegalDataValid = Object.values(legalData).every((el) => !!el);

    return isNumberValid && isExpDateValid && isCvvValid && isLegalDataValid;
  }, [card, legalData]);

  const customerDetails = useMemo(() => {
    if (mode === "modal") {
      const leadGuest = reservation?.sailings?.[0]?.cabins?.[0]?.guests?.[0];

      const resAddress = Array.isArray(leadGuest?.address)
        ? leadGuest?.address.join(" ")
        : leadGuest?.address;

      const details = {
        firstName: leadGuest?.given_name ?? "",
        lastName: leadGuest?.lastname ?? "",
        address: resAddress ?? "",
        city: leadGuest?.city ?? "",
        state: leadGuest?.state ?? "",
        zip: leadGuest?.zip_code ?? "",
        country: leadGuest?.country ?? "",
        email: reservation?.lead_email ?? "",
        phone: reservation?.lead_phone ?? "",
      };

      if (billingDetails) {
        if (billingDetails.name && billingDetails.name.trim().length > 1) {
          const nameParts = billingDetails.name.split(" ");

          details.firstName = nameParts[0] || details.firstName;
          details.lastName = nameParts.slice(1).join(" ") || details.lastName;
        }

        if (billingDetails.address?.city) {
          details.city = billingDetails.address.city;
        }

        if (billingDetails.address?.state) {
          details.state = billingDetails.address.state;
        }

        if (billingDetails.address?.postal_code) {
          details.zip = billingDetails.address.postal_code;
        }

        if (billingDetails.address?.country) {
          details.country = billingDetails.address.country;
        }
      }

      return details;
    }

    const leadGuest = guests?.[1]?.[1];

    return {
      firstName: leadGuest?.given_name ?? "",
      lastName: leadGuest?.last_name ?? "",
      address: `${leadGuest?.address1 ?? ""} ${
        leadGuest?.address2 ?? ""
      }`.trim(),
      city: leadGuest?.city ?? "",
      state: leadGuest?.state ?? "",
      zip: leadGuest?.zip_code ?? "",
      country: leadGuest?.country ?? "",
      email: leadGuest?.email ?? "",
      phone: leadGuest?.phone ?? "",
    };
  }, [mode, billingDetails, guests, reservation]);

  const transactions: [
    {
      full?: number;
      deposit?: number;
      amount: number;
      currency: string;
      made_by: string;
      transaction_type: string;
    },
  ] = useMemo(() => {
    if (mode === "modal") {
      return [
        {
          amount: amount ?? 0,
          currency: currency ?? "USD",
          made_by: "Website Authorize.net",
          transaction_type: "creditcard",
          full: undefined,
          deposit: undefined,
        },
      ];
    }

    const output = {
      full: 0,
      deposit: 0,
      amount: 0,
      currency: rooms?.[1].pricing?.payment_schedule?.[0].currency ?? "USD",
      made_by: "Website Authorize.net",
      transaction_type: "creditcard",
    };

    const deposit = Object.values(rooms ?? {}).reduce((sum, room) => {
      const roomDeposit = room?.pricing?.payment_schedule?.[0]?.amount ?? 0;

      return Number(sum) + Number(roomDeposit);
    }, 0);

    output.deposit = deposit;

    for (const key in rooms) {
      if (Object.prototype.hasOwnProperty.call(rooms, key)) {
        const paymentSchedule = rooms[+key].pricing?.payment_schedule;

        if (Array.isArray(paymentSchedule)) {
          paymentSchedule.forEach((payment) => {
            if (payment.amount) {
              output.full += parseFloat(payment.amount);
            }
          });
        }
      }
    }

    if (priceType === "full") {
      output.amount = output.full;
    }

    if (priceType === "deposit") {
      output.amount = output.deposit;
    }

    output.amount = Math.ceil(output.amount * 100) / 100;
    output.full = Math.ceil(output.full * 100) / 100;
    output.deposit = Math.ceil(output.deposit * 100) / 100;

    return [output];
  }, [rooms, priceType, mode, amount, currency]);

  const monthList = useMemo(() => {
    return getMonthsList().map((item) => ({ value: item, label: item }));
  }, []);

  const yearList = useMemo(() => {
    return getYearsList(20, 0).map((item) => ({
      value: String(item),
      label: String(item),
    }));
  }, []);

  const params = useMemo(() => {
    if (mode !== "page") {
      return "";
    }

    const totalRooms = +(searchParams.get("rooms") ?? 0);
    const currentRoomNumber = +(searchParams.get("room") ?? 0);

    const output = prepareParams(
      searchParams,
      rooms ?? {},
      currentRoomNumber,
      currentRoomNumber < totalRooms,
    );

    return output;
  }, [searchParams, rooms, mode]);

  const handleExpirationDateMonth = ({ value }: { value: string }) => {
    setDate((prev) => ({ ...prev, month: { value, errorMessage: "" } }));
  };

  const handleExpirationDateYear = ({ value }: { value: string }) => {
    setDate((prev) => ({ ...prev, year: { value, errorMessage: "" } }));
  };

  const handleInputChange = ({
    value,
    valueKey,
  }: {
    value: string;
    valueKey: string;
  }) => {
    setCard((prev) => ({ ...prev, [valueKey]: { errorMessage: "", value } }));
  };

  const handlePriceTypeChange = (type: typeof priceType) => setPriceType(type);

  const handleLegalDataChange = ({
    value,
    valueKey,
  }: {
    value: boolean;
    valueKey: string;
  }) => {
    setLegalData((prev) => ({ ...prev, [valueKey]: value }));
  };

  const setErrors = ({
    cardNumber,
    cvv,
    expirationDate,
    combinedLength,
  }: {
    cardNumber: boolean | string;
    cvv: boolean | string;
    expirationDate: boolean | string;
    combinedLength: boolean;
  }) => {
    setCard((prev) => {
      const output = structuredClone(prev);

      if (typeof cardNumber === "string") {
        output.cardNumber.errorMessage = cardNumber;
      } else if (!cardNumber) {
        output.cardNumber.errorMessage = "Card number is not valid";
      }

      if (typeof cvv === "string") {
        output.cvv.errorMessage = cvv;
      } else if (!cvv) {
        output.cvv.errorMessage = "CVV number is not valid";
      }

      if (typeof expirationDate === "string") {
        output.expirationDate.errorMessage = expirationDate;
      } else if (!expirationDate) {
        output.expirationDate.errorMessage = "Date is not valid";
      }

      if (!combinedLength) {
        output.cvv.errorMessage =
          "Combined length of card number and CVV should be 19";
      }

      return output;
    });
  };

  const defaultFail = (msg?: string) => {
    dispatch(
      showToast({
        type: TOAST.ERROR_TYPE,
        message: msg || "Payment failed",
        duration: TOAST.DEFAULT_DURATION,
      }),
    );
  };

  const defaultSuccess = () => {
    dispatch(
      showToast({
        type: TOAST.SUCCESS_TYPE,
        message: "Payment success",
        duration: TOAST.DEFAULT_DURATION,
      }),
    );
  };

  const onPaymentFail = (msg?: string) => {
    if (onFail) {
      onFail(msg);
    } else {
      defaultFail(msg);
    }
  };

  const onPaymentSuccess = () => {
    if (onSuccess) {
      onSuccess();

      return;
    }

    defaultSuccess();
    navigate(`/search-results/${cruiseId}/payment-confirmation?${params}`);
  };

  const acceptDataHandler = async (opaqueData: {
    opaqueData: { dataValue: string };
  }) => {
    try {
      const clonedTransictions = structuredClone(transactions[0]);

      if ("full" in clonedTransictions && "deposit" in clonedTransictions) {
        delete clonedTransictions.full;
        delete clonedTransictions.deposit;
      }

      const pnrToUse = pnr ?? rooms?.[1]?.pnr ?? "";

      const response = await createOpaqueData({
        pnr: pnrToUse,
        channel: "CS",
        channel_partner: agency,
        agency: agent,
        transactions: [
          {
            ...clonedTransictions,
            amount: `${clonedTransictions.amount}`,
            tokenized: opaqueData.opaqueData.dataValue,
          },
        ],
      });

      if (response.data.errors && response.isError) {
        onPaymentFail();
      } else {
        onPaymentSuccess();
      }
    } catch (error) {
      onPaymentFail();
      console.error("opaqueDataHandler error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const makeOpaqueRequet = async () => {
    try {
      const cardData = {
        cardNumber: card.cardNumber.value,
        year: card.expirationDate.value.split("-")[0],
        month: card.expirationDate.value.split("-")[1],
        cardCode: card.cvv.value,
      };

      const pnrToUse = pnr ?? rooms?.[1]?.pnr ?? "";

      const opaqueTransactionRequest = [
        {
          currency: transactions[0].currency ?? "USD",
          amount: transactions[0].amount,
          transaction_type: "creditcard",
          made_by: "Website",
          card: {
            pan: cardData.cardNumber,
            expiry_month: cardData.month,
            expiry_year: cardData.year,
            cvv: cardData.cardCode,
            name: `${customerDetails.firstName} ${customerDetails.lastName}`,
            address: {
              address: customerDetails.address,
              city: customerDetails.city,
              state: customerDetails.state,
              zip: customerDetails.zip,
              country: customerDetails.country,
            },
            email: customerDetails.email,
            phone: customerDetails.phone,
          },
        },
      ];

      const response = await createOpaqueData({
        pnr: pnrToUse,
        channel: "CS",
        channel_partner: agency,
        agency: agent,
        transactions: opaqueTransactionRequest,
      });

      if (response.data.errors && response.isError) {
        onPaymentFail();
      } else {
        onPaymentSuccess();
      }
    } catch (error) {
      onPaymentFail();
      console.error("opaqueDataHandler error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const payHandler = async () => {
    const authData = {
      apiLoginID: authorize_api_login_id,
      clientKey: authorize_client_key,
    };

    const cardData = {
      cardNumber: card.cardNumber.value,
      year: card.expirationDate.value.split("-")[0],
      month: card.expirationDate.value.split("-")[1],
      cardCode: card.cvv.value,
    };

    const pnrToUse = pnr ?? rooms?.[1]?.pnr ?? "";

    const acceptTransactionRequest = {
      amount: transactions[0].amount,
      transactionType: "authCaptureTransaction",
      description: "Cabin booking payment",
      billTo: {
        firstName: customerDetails.firstName,
        lastName: customerDetails.lastName,
        city: customerDetails.city,
        state: customerDetails.state,
        zip: customerDetails.zip,
        country: customerDetails.country,
      },
      refId: pnrToUse,
    };

    const secureData = {
      authData,
      cardData,
      transactionRequest: acceptTransactionRequest,
    };

    setIsLoading(true);

    if ("Accept" in window && isUseAcceptCore) {
      const accept: AcceptObject = window.Accept as AcceptObject;

      if (accept.dispatchData && typeof accept.dispatchData === "function") {
        accept.dispatchData(secureData, acceptDataHandler);
      }
    } else {
      makeOpaqueRequet();
    }
  };

  const validateForm = () => {
    const cardNumber = validateCreditCard(card.cardNumber.value);
    const cvv = validateCVV(card.cvv.value);
    const expirationDate = validateDate(card.expirationDate.value);

    const combinedLength =
      card.cardNumber.value.replace(/\s/g, "").length +
        card.cvv.value.length ===
      19;

    if (
      mode === "page" &&
      payment_option_text &&
      priceType === payment_option_text
    ) {
      navigate(`/search-results/${cruiseId}/payment-confirmation?${params}`);

      return;
    }

    if (!cardNumber || !cvv || !expirationDate || !combinedLength) {
      setErrors({
        cardNumber,
        cvv,
        expirationDate,
        combinedLength,
      });
    } else {
      payHandler();
    }
  };

  useEffect(() => {
    setCard((prevState) => ({
      ...prevState,
      expirationDate: {
        ...prevState.expirationDate,
        value:
          Boolean(date.year.value) && Boolean(date.month.value)
            ? `${date.year.value}-${date.month.value}`
            : "",
      },
    }));
  }, [date.month.value, date.year.value]);

  useEffect(() => {
    if (activeRestriction) {
      setCard((prev) => ({
        ...prev,
        cardNumber: {
          ...prev.cardNumber,
          errorMessage: activeRestriction.errorMessage,
        },
      }));
    } else {
      setCard((prev) => ({
        ...prev,
        cardNumber: {
          ...prev.cardNumber,
          errorMessage: "",
        },
      }));
    }
  }, [activeRestriction]);

  useEffect(() => {
    if (mode === "page") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [mode]);

  return (
    <LoadingContainer isLoading={!loading && !isCruiseLoading}>
      <div className={styles.container}>
        {!hideTitleAndPriceType && <h1 className={styles.title}>Payment</h1>}

        {Boolean(new_booking_payment_info) && mode === "page" && (
          <Alert message={new_booking_payment_info} />
        )}

        {!hideTitleAndPriceType && (
          <PriceType
            priceType={priceType}
            full={transactions[0].full ?? 0}
            deposit={transactions[0].deposit ?? 0}
            currency={transactions[0].currency ?? "USD"}
            onPriceTypeChange={handlePriceTypeChange}
          />
        )}

        <form className={styles.form} onSubmit={validateForm}>
          <div className={styles.inputs}>
            <Input
              className={styles.input}
              value={card.cardNumber.value}
              valueKey="cardNumber"
              label="Card number"
              placeholder="Enter 16 digits"
              errorMessage={card.cardNumber.errorMessage}
              onChange={handleInputChange}
              disabled={Boolean(
                mode === "page" &&
                payment_option_text &&
                priceType === payment_option_text,
              )}
            />
          </div>

          <div className={styles.inputsDate}>
            <CustomSelect
              className={styles.input}
              value={date.month.value}
              label="month"
              errorMessage={card.expirationDate.errorMessage}
              items={monthList}
              placeholder="Month"
              onChange={handleExpirationDateMonth}
              disabled={Boolean(
                mode === "page" &&
                payment_option_text &&
                priceType === payment_option_text,
              )}
            />

            <CustomSelect
              className={styles.input}
              value={date.year.value}
              label="year"
              errorMessage={card.expirationDate.errorMessage}
              items={yearList}
              placeholder="Year"
              onChange={handleExpirationDateYear}
              disabled={Boolean(
                mode === "page" &&
                payment_option_text &&
                priceType === payment_option_text,
              )}
            />
          </div>

          <div className={styles.inputs}>
            <Input
              className={styles.input}
              value={card.cvv.value}
              valueKey="cvv"
              label="Card Code"
              placeholder="3 digits"
              errorMessage={card.cvv.errorMessage}
              onChange={handleInputChange}
              disabled={Boolean(
                mode === "page" &&
                payment_option_text &&
                priceType === payment_option_text,
              )}
            />
          </div>

          <LegalData
            checkbox_1_checked={legalData.checkbox_1}
            checkbox_2_checked={legalData.checkbox_2}
            checkbox_3_checked={legalData.checkbox_3}
            checkbox_4_checked={legalData.checkbox_4}
            checkbox_5_checked={legalData.checkbox_5}
            checkbox_6_checked={legalData.checkbox_6}
            onChange={handleLegalDataChange}
          />
        </form>

        {Boolean(new_booking_payment_info) && mode === "page" && (
          <Alert message={new_booking_payment_info} />
        )}

        <div className={styles.inputs}>
          <Button
            onClick={validateForm}
            className={styles.submitButton}
            label="Pay"
            loading={isLoading}
            disabled={
              !(
                mode === "page" &&
                payment_option_text &&
                payment_option_text === priceType
              ) &&
              (!isFormValid || isLoading)
            }
          />
        </div>
      </div>
    </LoadingContainer>
  );
}

export default AuthorizePayment;
